/**
 * Money handling.
 *
 * Every monetary value in this system crosses a boundary where a float would
 * silently lose precision: Postgres NUMERIC, the QBO and Clio JSON APIs, and
 * the browser. We therefore carry money as integer minor units (cents) inside
 * the application and only format at the edges.
 *
 * Rationale: `0.1 + 0.2 !== 0.3` in IEEE-754. On a trust ledger that
 * discrepancy is an IOLTA compliance problem, not a rounding nit.
 */
export const ZERO = 0;
/** Wraps an integer number of cents. Throws on a non-integer input. */
export function minor(cents) {
    if (!Number.isInteger(cents)) {
        throw new TypeError(`Money must be integer minor units, received ${cents}`);
    }
    return cents;
}
/**
 * Parses a decimal string (the form Postgres NUMERIC and both upstream APIs
 * return) into minor units without ever constructing an intermediate float.
 */
export function fromDecimalString(value) {
    const trimmed = value.trim();
    const match = /^(-)?(\d+)(?:\.(\d+))?$/.exec(trimmed);
    if (!match) {
        throw new TypeError(`Cannot parse money from ${JSON.stringify(value)}`);
    }
    const [, sign, whole = '0', fraction = ''] = match;
    const cents = `${fraction}00`.slice(0, 2);
    const total = Number(whole) * 100 + Number(cents);
    return ((sign === '-' ? -total : total) | 0);
}
/** Serialises minor units back to the decimal string shape Postgres expects. */
export function toDecimalString(value) {
    const negative = value < 0;
    const abs = Math.abs(value);
    const whole = Math.floor(abs / 100);
    const cents = String(abs % 100).padStart(2, '0');
    return `${negative ? '-' : ''}${whole}.${cents}`;
}
export function add(a, b) {
    return (a + b);
}
export function subtract(a, b) {
    return (a - b);
}
export function sum(values) {
    return values.reduce((acc, v) => add(acc, v), ZERO);
}
/** Formats for display. Presentation only — never feed this back into maths. */
export function format(value, currency = 'USD', locale = 'en-US') {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value / 100);
}
/**
 * Scales an amount by a rate in basis points (10,000 bps = 100%).
 *
 * Rates enter this codebase as basis points rather than fractions for the same
 * reason amounts are cents: a percentage carried as 0.915 is a float, and a
 * realization rate multiplied into a receivable balance is exactly where that
 * starts to matter. Rounds half away from zero so the projection does not drift
 * toward zero across a few hundred matters.
 */
export function scaleByBps(value, bps) {
    if (!Number.isInteger(bps)) {
        throw new TypeError(`Rate must be integer basis points, received ${bps}`);
    }
    const scaled = (Math.abs(value) * bps) / 10_000;
    const rounded = Math.round(scaled);
    return ((value < 0 ? -rounded : rounded) | 0);
}
/**
 * Ratio of two amounts as basis points, for deriving a rate from history.
 * A zero denominator has no rate — the caller decides what to assume, and is
 * made to say so rather than being handed a silent zero.
 */
export function ratioToBps(part, whole) {
    if (whole === 0)
        return null;
    return Math.round((part / whole) * 10_000);
}
/** Basis points as a display percentage. Presentation only. */
export function formatBps(bps, locale = 'en-US') {
    return new Intl.NumberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
    }).format(bps / 10_000);
}
/** Never below zero. Trust "available" is a floor, not a signed balance. */
export function clampAtZero(value) {
    return (value < 0 ? 0 : value);
}
/**
 * The smaller of two amounts. Named for the type rather than `min`, because
 * this module's exports land in a flat package namespace where a bare `min`
 * says nothing about what it can be applied to.
 */
export function minMinor(a, b) {
    return (a < b ? a : b);
}
//# sourceMappingURL=money.js.map